import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase, hasSupabaseCredentials } from '../lib/supabaseClient';
import { useDocumentMeta } from '../lib/useDocumentMeta';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  published_at: string;
  tags: string[];
  slug: string;
  featured_image?: string;
  read_time: number;
  status?: 'draft' | 'published' | 'archived';
  published?: boolean;
}

const fallbackPosts: BlogPost[] = [
  {
    id: 1,
    title: 'Why AI Is a Skill, Not a Subject',
    excerpt: 'You cannot learn AI by reading about it any more than you can learn tennis by watching a video. The gap between knowing about AI and being good at it is practice. Here is why that distinction changes everything about how you should approach it.',
    content: '',
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['AI Literacy', 'Learning', 'Practice'],
    slug: 'why-ai-is-a-skill-not-a-subject',
    read_time: 6,
    featured_image: 'https://images.pexels.com/photos/5905709/pexels-photo-5905709.jpeg',
    status: 'published',
    published: true,
  },
  {
    id: 2,
    title: 'How Daily Challenges Beat Courses for Learning AI',
    excerpt: 'Courses teach you what AI can do. Daily challenges teach you how to do it. The difference matters because knowledge you do not practice decays in days, while skills you practice compound over weeks.',
    content: '',
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['Learning', 'Practice', 'Challenges'],
    slug: 'how-daily-challenges-beat-courses',
    read_time: 7,
    featured_image: 'https://images.pexels.com/photos/4144923/pexels-photo-4144923.jpeg',
    status: 'published',
    published: true,
  },
  {
    id: 3,
    title: 'The 10 AI Skills That Actually Matter in 2026',
    excerpt: 'Prompt engineering gets the hype, but it is one of ten skills that determine whether you are actually good at using AI. Here is the full list, what each one means, and why most people ignore the ones that matter most.',
    content: '',
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['Skills', 'AI Literacy', 'Practice'],
    slug: 'ten-ai-skills-that-matter',
    read_time: 9,
    featured_image: 'https://images.pexels.com/photos/4260477/pexels-photo-4260477.jpeg',
    status: 'published',
    published: true,
  },
  {
    id: 4,
    title: 'How AI Grading Works and Why It Accelerates Learning',
    excerpt: 'Getting feedback on your work is the single fastest way to improve at any skill. When the feedback comes from AI, it is instant, specific, and consistent. Here is how that loop works and why it changes the learning curve.',
    content: '',
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['Grading', 'Feedback', 'Learning'],
    slug: 'how-ai-grading-works',
    read_time: 8,
    featured_image: 'https://images.pexels.com/photos/8612967/pexels-photo-8612967.jpeg',
    status: 'published',
    published: true,
  },
  {
    id: 5,
    title: 'Bring Your Own Problem: Turning Real Work Into Practice',
    excerpt: 'The best way to practice a skill is on something you actually need to do. Bring Your Own Problem takes your real task and turns it into a guided AI coaching session that teaches skills as you make progress.',
    content: '',
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['Practice', 'Coaching', 'Product'],
    slug: 'bring-your-own-problem-real-work',
    read_time: 8,
    featured_image: 'https://images.pexels.com/photos/8423025/pexels-photo-8423025.jpeg',
    status: 'published',
    published: true,
  },
  {
    id: 6,
    title: 'Why Streaks Work for AI Skill-Building',
    excerpt: 'A seven-minute daily streak is not a gimmick. It is the mechanism that turns a one-time effort into a compounding skill. Here is the research behind why short, consistent practice beats long, irregular sessions.',
    content: '',
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['Streaks', 'Practice', 'Learning'],
    slug: 'why-streaks-work-ai-skills',
    read_time: 7,
    featured_image: 'https://images.pexels.com/photos/4145032/pexels-photo-4145032.jpeg',
    status: 'published',
    published: true,
  },
  {
    id: 7,
    title: 'Prompt Engineering Is Overrated. Here Is What Actually Matters.',
    excerpt: 'Everyone talks about writing the perfect prompt. Almost no one talks about the skills that actually determine whether AI output is useful: verification, critique, context-setting, and knowing when not to trust the result.',
    content: '',
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['Prompting', 'Skills', 'AI Literacy'],
    slug: 'prompt-engineering-overrated',
    read_time: 9,
    featured_image: 'https://images.pexels.com/photos/3933250/pexels-photo-3933250.jpeg',
    status: 'published',
    published: true,
  },
  {
    id: 8,
    title: 'From Beginner to Confident: What One Month of Daily Practice Looks Like',
    excerpt: 'What happens when you commit to one seven-minute AI challenge every day for 30 days? The skill score moves, but the bigger change is in how you think about AI. Here is the arc, week by week.',
    content: '',
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['Practice', 'Beginners', 'Progress'],
    slug: 'one-month-of-daily-practice',
    read_time: 7,
    featured_image: 'https://images.pexels.com/photos/4262414/pexels-photo-4262414.jpeg',
    status: 'published',
    published: true,
  },
];

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const BlogPage = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [allTags, setAllTags] = useState<string[]>([]);

  useDocumentMeta({
    title: 'Blog — AI Skills, Practice, and Daily Challenges | PromptGPT',
    description: 'Practical writing on AI skills, daily practice, prompt engineering, and learning to use AI well. No hype, no theory — just field notes from building a daily AI workout.',
    canonical: 'https://promptgpt.ai/blog',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'PromptGPT Blog',
      description: 'Practical writing on AI skills, daily practice, and learning to use AI well.',
      url: 'https://promptgpt.ai/blog',
    },
  });

  useEffect(() => {
    loadBlogPosts();
  }, []);

  const loadBlogPosts = async () => {
    setLoading(true);

    if (!hasSupabaseCredentials) {
      setPosts(fallbackPosts);
      extractTags(fallbackPosts);
      setLoading(false);
      return;
    }

    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 5000)
      );

      const fetchPromise = supabase.rpc('get_published_blog_posts');

      const { data, error } = (await Promise.race([fetchPromise, timeoutPromise])) as any;

      if (error) throw error;

      if (data && data.length > 0) {
        setPosts(data);
        extractTags(data);
      } else {
        setPosts(fallbackPosts);
        extractTags(fallbackPosts);
      }
    } catch {
      setPosts(fallbackPosts);
      extractTags(fallbackPosts);
    } finally {
      setLoading(false);
    }
  };

  const extractTags = (posts: BlogPost[]) => {
    const tags = new Set<string>();
    posts.forEach((post) => {
      post.tags?.forEach((tag: string) => tags.add(tag));
    });
    setAllTags(Array.from(tags));
  };

  const filteredPosts = posts.filter((post) => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      post.title.toLowerCase().includes(q) || post.excerpt.toLowerCase().includes(q);
    const matchesTag = !selectedTag || post.tags?.includes(selectedTag);
    return matchesSearch && matchesTag;
  });

  const featured = filteredPosts[0];
  const rest = filteredPosts.slice(1);

  return (
    <div className="bg-warm-50 min-h-screen">
      {/* Hero */}
      <section className="border-b border-ink-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 pt-28 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-4 bg-white border border-ink-100 rounded-full px-3 py-1.5">
              Field notes
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-5 text-ink-900">
              Notes from the lab.
            </h1>
            <p className="text-xl text-ink-600 max-w-2xl leading-relaxed">
              Honest writing on AI skills, daily practice, and what it actually
              takes to get good at using AI well.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-ink-100 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
              <input
                type="text"
                placeholder="Search articles…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-warm-50 border border-ink-100 rounded-full text-ink-900 placeholder-ink-400 focus:outline-none focus:border-accent-500 focus:bg-white transition"
              />
            </div>
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className="px-4 py-3 bg-warm-50 border border-ink-100 rounded-full text-ink-700 focus:outline-none focus:border-accent-500 focus:bg-white transition appearance-none cursor-pointer"
            >
              <option value="">All topics</option>
              {allTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16">
        {loading && (
          <div className="text-center py-24">
            <div className="inline-block h-8 w-8 rounded-full border-2 border-ink-100 border-t-accent-600 animate-spin" />
            <p className="text-ink-500 mt-4 text-sm">Loading articles…</p>
          </div>
        )}

        {/* Featured */}
        {!loading && featured && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-16"
          >
            <Link
              to={`/blog/${featured.slug}`}
              className="group grid md:grid-cols-2 gap-8 lg:gap-12 items-center bg-white border border-ink-100 rounded-3xl p-6 md:p-8 hover:border-ink-200 hover:shadow-soft transition-all"
            >
              {featured.featured_image && (
                <div className="aspect-[4/3] md:aspect-[5/4] overflow-hidden rounded-2xl bg-warm-100">
                  <img
                    src={featured.featured_image}
                    alt={featured.title}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
              )}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="chip-accent">Featured</span>
                  {featured.tags?.slice(0, 2).map((tag) => (
                    <span key={tag} className="chip">
                      {tag}
                    </span>
                  ))}
                </div>
                <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight text-ink-900 mb-4 group-hover:text-accent-700 transition-colors">
                  {featured.title}
                </h2>
                <p className="text-ink-600 text-lg leading-relaxed mb-6">
                  {featured.excerpt}
                </p>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-5 text-sm text-ink-500">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-4 h-4" />
                      {formatDate(featured.published_at)}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      {featured.read_time} min read
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-900 group-hover:text-accent-700 transition-colors">
                    Read essay
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        )}

        {/* Grid */}
        {!loading && rest.length > 0 && (
          <div>
            <div className="flex items-baseline justify-between mb-8 pb-4 border-b border-ink-100">
              <h3 className="font-display text-2xl tracking-tight text-ink-900">
                More writing
              </h3>
              <span className="text-sm text-ink-500">{rest.length} articles</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * index, duration: 0.3 }}
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="group block bg-white border border-ink-100 rounded-2xl overflow-hidden hover:border-ink-200 hover:shadow-soft transition-all h-full"
                  >
                    {post.featured_image && (
                      <div className="aspect-[16/10] overflow-hidden bg-warm-100">
                        <img
                          src={post.featured_image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.tags?.slice(0, 2).map((tag) => (
                          <span key={tag} className="chip">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="font-display text-xl leading-snug tracking-tight text-ink-900 mb-2 group-hover:text-accent-700 transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-sm text-ink-600 leading-relaxed mb-5 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-ink-500 pt-4 border-t border-ink-100">
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.published_at)}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="w-3 h-3" />
                          {post.read_time} min
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        )}

        {/* Empty */}
        {!loading && filteredPosts.length === 0 && (
          <div className="text-center py-24">
            <p className="font-display text-2xl text-ink-900 mb-2">No articles found</p>
            <p className="text-ink-500">Try a different search or topic.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
